import { EventDefinition } from 'redux-beacon'
import { LocationChangePayload } from '@chegg-wt/core/state/app/types'
import { Config } from '@chegg-wt/core/config'
import { AuthModalOrigin } from '@chegg-wt/core/components/Modals/AuthModal/types'
import {
  TYPE_CITATIONS,
  TYPE_GRAMMAR,
  TYPE_PLAGIARISM,
  HOMEPAGE_FEATURE_TOUR,
  TYPE_EXPERT_CHECK,
} from '@chegg-wt/core/constants/plusTab'
import {
  CLOSE,
  LOGIN,
  CREATE_ACCOUNT,
} from '@chegg-wt/core/components/Modals/UnlockAccountGateModal/constants'
import {
  TYPE_CREATE_CITATIONS,
  TYPE_CHECK_PAPER,
  HOMEPAGE_CARD,
} from '@chegg-wt/core/constants/card'
import {
  getSelectedUserTypeSelector,
  getSelectedSchoolTypeSelector,
} from '@chegg-wt/core/state/user/selectors'
import { routeToRioPageNameMap } from '../utils/pageNames'
import {
  SyntheticLocationChangePayload,
  INSTRUMENTATION_SIGNUP_START,
  VIEW_AUTH_SIGN_UP_MODE,
  INSTRUMENTATION_SIGNIN_START,
  VIEW_AUTH_SIGN_IN_MODE,
} from '../actions'
import { EVENT_TYPES } from './constants'
import { EventWithConfig } from './types'
import { getUpdatedView } from './utils'
import {
  ClickstreamInteractionPayload,
  ClickstreamInteractionTransaction,
  UserClickPayload,
  origins,
  interactions,
  transactions,
} from './clickstreamInteractions'
import {
  ClickstreamComponentViewPayload,
  componentOrigins,
  ComponentPayload,
  componentViews,
} from './clickstreamComponentViews'
import {
  SignupStartPayload,
  signUpStartElementName,
  linkType,
} from './clickstreamSignupStart'

import { SigninStartPayload } from './clickstreamSignInStart'
import { ClickstreamView1 } from './rsw/eventSchemas'

export const locationChangeEvent: EventDefinition = (
  action
): EventWithConfig | undefined => {
  const {
    routeName,
    pageName,
  }: LocationChangePayload | SyntheticLocationChangePayload = action.payload
  // todo - create a separate clickstreamview function later, this function should not be modified like this
  const page = pageName || routeToRioPageNameMap[routeName]

  if (!page) {
    return
  }

  const view = getUpdatedView({
    site: Config.tokenSiteCode,
    page,
  })

  const data: ClickstreamView1 = {}
  const eventname = routeName === 'toWritingAssistant' && 'view draft page'
  if (eventname) {
    data.eventname = eventname
  }

  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_VIEW,
      data,
    },
    config: {
      eventVersion: 3,
      stateUpdate: {
        view,
      },
    },
  }
}

export const userClickEvents = (
  payload: ClickstreamInteractionPayload,
  transaction?: ClickstreamInteractionTransaction
): EventWithConfig => {
  const {
    name,
    region,
    text,
    type,
    interactionType,
    value,
    id,
    force,
    options,
    style: citation_style,
    eventname,
    citationMetadata,
  } = payload
  if (transaction) {
    const { metadata, type: transactionType } = transaction
    return {
      payload: {
        type: EVENT_TYPES.CLICKSTREAM_INTERACTION,
        data: {
          eventname,
          interaction: {
            element: { name, type, text, region_name: region, value, id },
            type: interactionType ?? 'click',
          },
          transaction: {
            type: transactionType,
            metadata,
          },
        },
        ...(options ? { options } : {}),
      },
      config: {
        eventVersion: 5,
        force,
      },
    }
  }

  let metadata: any
  if (citation_style) {
    metadata = { style: citation_style }
  }
  if (citationMetadata) {
    metadata = citationMetadata
  }

  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_INTERACTION,
      data: {
        eventname,
        interaction: {
          element: { name, type, text, region_name: region, value, id },
          type: interactionType ?? 'click',
        },
        ...(metadata
          ? {
              content: {
                metadata:
                  metadata.bibliographyId || metadata.word_count
                    ? {
                        wt: {
                          writing: {
                            bibliography_id: metadata.bibliographyId,
                            word_count: metadata.word_count,
                          },
                        },
                      }
                    : { citation: metadata },
              },
            }
          : {}),
      },
      ...(options ? { options } : {}),
    },
    config: {
      eventVersion: 5,
      force,
    },
  }
}

export const userComponentEvents = (payload: ClickstreamComponentViewPayload) => {
  const { name, region, type, text, value, id, metadata } = payload

  if (!metadata?.source) {
    // remove empty key
    delete metadata?.source
  }
  // use new rio version for clickstreamview events having metadata
  const eventVersion = metadata ? 5 : 3
  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_COMPONENT_VIEW,
      data: {
        component_view: {
          component: {
            name,
            type,
            region_name: region,
            text,
            value,
            id,
          },
        },
        ...(metadata
          ? {
              content: {
                metadata: metadata.bibliographyId
                  ? {
                      wt: { writing: { bibliography_id: metadata.bibliographyId } },
                    }
                  : { citation: metadata },
              },
            }
          : {}),
      },
    },
    config: {
      eventVersion,
    },
  }
}

export const clickstreamInteractionEvent: EventDefinition = (action: {
  payload: UserClickPayload
}): EventWithConfig => {
  const {
    eventname,
    type,
    origin,
    value,
    id,
    step,
    region,
    name,
    options,
    text,
    style,
    interactionType,
    citationMetadata,
  } = action.payload
  switch (origin) {
    // Holistic Dashboard events
    case origins.dashboardClickSource:
      return userClickEvents(interactions.dashboardClickSource(value))
    case origins.dashboardClickCitationCTA:
      return userClickEvents(interactions.dashboardClickCitationCTA())
    case origins.dashboardCreateAccountCitationWidgetCTA:
      return userClickEvents(interactions.dashboardCreateAccountCitationWidgetCTA())
    case origins.dashboardSignInCitationWidgetCTA:
      return userClickEvents(interactions.dashboardSignInCitationWidgetCTA())
    case origins.dashboardCreateAccountPaperUploadWidgetCTA:
      return userClickEvents(interactions.dashboardCreateAccountPaperUploadWidgetCTA())
    case origins.dashboardSignInPaperUploadWidgetCTA:
      return userClickEvents(interactions.dashboardSignInPaperUploadWidgetCTA())
    case origins.dashboardClickPaperSeeAll:
      return userClickEvents(interactions.dashboardClickPaperSeeAll())
    case origins.dashboardClickPaper:
      return userClickEvents(interactions.dashboardClickPaper())
    case origins.dashboardClickGPCTA:
      return userClickEvents(interactions.dashboardClickGPCTA())
    case origins.dashboardClickECCTA:
      return userClickEvents(interactions.dashboardClickECCTA())
    case origins.dashboardClickProjectSeeAll:
      return userClickEvents(interactions.dashboardClickProjectSeeAll())
    case origins.dashboardClickProjectCitation:
      return userClickEvents(interactions.dashboardClickProjectCitation())
    case origins.dashboardCheckMyPaperUploadWidgetCTA:
      return userClickEvents(interactions.dashboardCheckMyPaperUploadWidgetCTA())
    case origins.dashboardMyComputerUploadWidgetCTA:
      return userClickEvents(interactions.dashboardMyComputerUploadWidgetCTA())
    case origins.dashboardGoogleDriveUploadWidgetCTA:
      return userClickEvents(interactions.dashboardGoogleDriveUploadWidgetCTA())
    case origins.dashboardTourStep:
      return userClickEvents(
        interactions.dashboardTourStep(),
        transactions.dashboardTourTransactionStep(step)
      )

    // UnlockAccountGateModal events
    case `${AuthModalOrigin.CFC_VIEWPAGE_EXIT_GUEST}-${CLOSE}`:
    case origins.closeModalCreateAccountExitPopup:
      return userClickEvents(interactions.closeModalCreateAccountExitPopup())
    case `${AuthModalOrigin.CFC_VIEWPAGE_EXIT_GUEST}-${LOGIN}`:
    case origins.signInExitPopup:
      return userClickEvents(interactions.signInExitPopup())
    case `${AuthModalOrigin.CFC_VIEWPAGE_EXIT_GUEST}-${CREATE_ACCOUNT}`:
    case origins.createAccountExitPopup:
      return userClickEvents(interactions.createAccountExitPopup())
    case `${AuthModalOrigin.ACCOUNT_STYLE_GATE_VIEWPAGE}-${LOGIN}`:
    case origins.createAccountStyleGateModalClickSignIn:
      return userClickEvents(interactions.createAccountStyleGateModalClickSignIn())
    case origins.citationHomepageModalSignIn:
      return userClickEvents(interactions.citationHomepageModalSignIn())
    case `${AuthModalOrigin.CITATION_FORM_PAGE}-${LOGIN}`:
      return userClickEvents(interactions.citationHomepageModalSignIn(options))
    case `${AuthModalOrigin.ACCOUNT_STYLE_GATE_VIEWPAGE}-${CREATE_ACCOUNT}`:
    case origins.createAccountStyleGateModalCTA:
      return userClickEvents(interactions.createAccountStyleGateModalCTA())
    case `${AuthModalOrigin.ACCOUNT_STYLE_GATE_VIEWPAGE}-${CLOSE}`:
    case origins.closeCreateAccountStyleGateModal:
      return userClickEvents(interactions.closeCreateAccountStyleGateModal())
    case origins.createAccountUnlockModal:
      return userClickEvents(interactions.createAccountUnlockModal())
    case `${AuthModalOrigin.CITATION_FORM_PAGE}-${CREATE_ACCOUNT}`:
      return userClickEvents(interactions.createAccountUnlockModal(options))
    case origins.accountGateModalClickUnlock:
      return userClickEvents(interactions.accountGateModalClickUnlock(options))

    // PlagiarismScrollModal events
    case origins.closeModalCheckPaperExitPopup:
      return userClickEvents(interactions.closeModalCheckPaperExitPopup())
    case origins.checkPaperExitPopup:
      return userClickEvents(interactions.checkPaperExitPopup())

    // PlusTabPanel events
    case `${HOMEPAGE_FEATURE_TOUR}-${TYPE_CITATIONS}`:
    case origins.homePageCitationsFeatureTourCTA:
      return userClickEvents(interactions.homePageCitationsFeatureTourCTA())
    case `${HOMEPAGE_FEATURE_TOUR}-${TYPE_GRAMMAR}`:
    case origins.homePageGrammarFeatureTourCTA:
      return userClickEvents(interactions.homePageGrammarFeatureTourCTA())
    case `${HOMEPAGE_FEATURE_TOUR}-${TYPE_PLAGIARISM}`:
    case origins.homePagePlagiarismFeatureTourCTA:
      return userClickEvents(interactions.homePagePlagiarismFeatureTourCTA())
    case `${HOMEPAGE_FEATURE_TOUR}-${TYPE_EXPERT_CHECK}`:
    case origins.homePageExpertCheckFeatureTourCTA:
      return userClickEvents(interactions.homePageExpertCheckFeatureTourCTA())

    // BibliographyContainer events
    case origins.copyInTextCitationMenuCTA:
      return userClickEvents(interactions.copyInTextCitationMenuCTA())
    case origins.copyInTextCitationHoverCTA:
      return userClickEvents(interactions.copyInTextCitationHoverCTA())
    case origins.styleChangeClick:
      return userClickEvents(interactions.styleChangeClick())
    case origins.styleChangeSelect:
      return userClickEvents(interactions.styleChangeSelect(value, style))

    // CitationsDashboard events
    case origins.citationsDashClickSource:
      return userClickEvents(interactions.citationsDashClickSource(value))
    case origins.citationsDashClickCTA:
      return userClickEvents(interactions.citationsDashClickCTA())
    case origins.citationsDashCreateAccountCitationListCTA:
      return userClickEvents(interactions.citationsDashCreateAccountCitationListCTA())
    case origins.citationsDashSignInCitationListCTA:
      return userClickEvents(interactions.citationsDashSignInCitationListCTA())

    // CitationsUpsellWrapper events
    case origins.citationsDashCreateAccountCitationWidgetCTA:
      return userClickEvents(interactions.citationsDashCreateAccountCitationWidgetCTA())
    case origins.citationsDashSignInCitationWidgetCTA:
      return userClickEvents(interactions.citationsDashSignInCitationWidgetCTA())

    // CardWithLink events
    case `${HOMEPAGE_CARD}-${TYPE_CREATE_CITATIONS}`:
    case origins.homePageMainCitationsCardCTA:
      return userClickEvents(interactions.homePageMainCitationsCardCTA())
    case `${HOMEPAGE_CARD}-${TYPE_CHECK_PAPER}`:
    case origins.homePageMainWritingCardCTA:
      return userClickEvents(interactions.homePageMainWritingCardCTA())

    // WorldCat events
    case origins.worldCatLogoClick:
      return userClickEvents(interactions.worldCatLogoClick())
    case origins.findAtMyLibraryClick:
      return userClickEvents(interactions.findAtMyLibraryClick())

    // FormPage events
    case origins.citationsFlowClickCreate:
      return userClickEvents(
        interactions.citationsFlowClickCreate(value, citationMetadata)
      )
    case origins.citationsFlowClickUpdate:
      return userClickEvents(
        interactions.citationsFlowClickUpdate(value, citationMetadata)
      )
    case origins.createManualCitationCTA:
      return userClickEvents(interactions.createManualCitationCTA(region))
    case origins.formPageAddAnnotationCTA:
      return userClickEvents(interactions.formPageAddAnnotationCTA())
    case origins.formPageAddContributorsCTA:
      return userClickEvents(interactions.formPageAddContributorsCTA())

    // Bibliography page events
    case origins.copyBibliographyCitationClick:
      return userClickEvents(interactions.copyBibliographyCitationClick())
    case origins.copyAllCitationsClick:
      return userClickEvents(interactions.copyAllCitationsClick())
    case origins.exportAllCitationsClick:
      return userClickEvents(interactions.exportAllCitationsClick())
    case origins.addNoteClick:
      return userClickEvents(interactions.addNoteClick())
    case origins.deleteCitationClick:
      return userClickEvents(interactions.deleteCitationClick())
    case origins.editCitationClick:
      return userClickEvents(interactions.editCitationClick())
    case origins.reviewCitationClick:
      return userClickEvents(interactions.reviewCitationClick())
    case origins.ignoreValidationClick:
      return userClickEvents(interactions.ignoreValidationClick())
    case origins.visitSourceLinkClick:
      return userClickEvents(interactions.visitSourceLinkClick())
    case origins.createNewCitationClick:
      return userClickEvents(interactions.createNewCitationClick())
    case origins.copyBibliographyCitationFromMenu:
      return userClickEvents(interactions.copyBibliographyCitationFromMenu())

    case origins.exportSelectedCitationsClick:
      return userClickEvents(interactions.exportSelectedCitationsClick())
    case origins.deleteSelectedCitationsClick:
      return userClickEvents(interactions.deleteSelectedCitationsClick())
    case origins.copySelectedCitationsClick:
      return userClickEvents(interactions.copySelectedCitationsClick())

    // UnlockModal events
    case origins.unlockModalSponsoredClick:
      return userClickEvents(interactions.unlockModalSponsoredClick(options))
    case origins.unlockModalSubscribeClick:
      return userClickEvents(interactions.unlockModalSubscribeClick(value, options))
    case origins.unlockModalSignInClick:
      return userClickEvents(interactions.unlockModalSignInClick(options))
    case origins.createFreeAccountAdUnlockModalClick:
      return userClickEvents(interactions.createFreeAccountAdUnlockModalClick())
    case origins.tryItForFreeTodayAdUnlockModalClick:
      return userClickEvents(interactions.tryItForFreeTodayAdUnlockModalClick())

    // ExportModal page events
    case origins.exportModuleExportCitationsClick:
      return userClickEvents(interactions.exportModuleExportCitationsClick(value))

    // CopySuccess modal events
    case origins.checkPaperAfterCopy:
      return userClickEvents(interactions.checkPaperAfterCopy())

    // PaperCheckerWidget events
    case origins.checkPaperAfterLogout:
      return userClickEvents(interactions.checkPaperAfterLogout())

    // Home page Pricing table events
    case origins.signUpClick:
      return userClickEvents(interactions.signUpClick(value))

    // improved book/journal search
    case origins.lookForWebsiteAfterSearchResult:
      return userClickEvents(interactions.lookForWebsiteAfterSearchResult())
    case origins.searchWithJstorAfterSearchResult:
      return userClickEvents(interactions.searchWithJstorAfterSearchResult())
    case origins.searchWithCrossrefAfterSearchResult:
      return userClickEvents(interactions.searchWithCrossrefAfterSearchResult())

    // Plagiarism Upsell card events
    case origins.plagiarismUpsellCardCTA:
      return userClickEvents(interactions.plagiarismUpsellCardCTA(name))

    // Gated events
    case origins.gatedExportContinueCTA:
      return userClickEvents(interactions.gatedExportContinueCTA(text))
    case origins.gatedExportUpgradeCTA:
      return userClickEvents(interactions.gatedExportUpgradeCTA(text))
    case origins.citationLimitApproachingContinueCTA:
      return userClickEvents(interactions.citationLimitApproachingContinueCTA(text))
    case origins.citationLimitApproachingUpgradeCTA:
      return userClickEvents(interactions.citationLimitApproachingUpgradeCTA(text))
    case origins.citationLimitReachedUpgradeCTA:
      return userClickEvents(interactions.citationLimitReachedUpgradeCTA(text))
    case origins.foldersPageExpirationUpgradeCTA:
      return userClickEvents(interactions.foldersPageExpirationUpgradeCTA(text))
    case origins.bibPageExpirationContinueCTA:
      return userClickEvents(interactions.bibPageExpirationContinueCTA(text))
    case origins.bibPageExpirationUpgradeCTA:
      return userClickEvents(interactions.bibPageExpirationUpgradeCTA(text))
    case origins.citationStyleClick:
      return userClickEvents(interactions.citationStyleClick(text))
    case origins.moreCitationsStyleClick:
      return userClickEvents(interactions.moreCitationsStyleClick(interactionType))

    // Top nav items in site click events
    case origins.topSideNavItemClick:
      return userClickEvents(interactions.topSideNavItemClick(value, text))
    case origins.topNavItemClick:
      return userClickEvents(interactions.topNavItemClick(value, text))

    // Site logo click event
    case origins.siteLogoClick:
      return userClickEvents(interactions.siteLogoClick(name))

    // upgrade CTA click events
    case origins.upgradeTopNavClick:
      return userClickEvents(interactions.upgradeTopNavClick())
    case origins.upgradeSourcePageCTA:
      return userClickEvents(interactions.upgradeSourcePageCTA(eventname, name))
    case origins.upgradeSearchPageCTA:
      return userClickEvents(interactions.upgradeSearchPageCTA(eventname, name))
    case origins.priceModalClaimOfferClick:
      return userClickEvents(interactions.priceModalClaimOfferClick(text))
    case origins.freeCTAUpgradeClick:
      return userClickEvents(interactions.freeCTAUpgradeClick())
    case origins.changeCitationStyleModalContinueClick:
      return userClickEvents(interactions.changeCitationStyleModalContinueClick())
    case origins.upgradeBannerCTA:
      return userClickEvents(interactions.upgradeBannerCTA())
    case origins.newCitationListModalCreateClick:
      return userClickEvents(interactions.newCitationListModalCreateClick())
    case origins.newCitationListModalStartClick:
      return userClickEvents(interactions.newCitationListModalStartClick())

    // proofReader events
    case origins.proofReaderReviewClick:
      return userClickEvents(interactions.proofReaderReviewClick(value))
    case origins.proofReaderRewriteClick:
      return userClickEvents(interactions.proofReaderRewriteClick(text))
    case origins.proofReaderExpandClick:
      return userClickEvents(interactions.proofReaderExpandClick(text))
    case origins.proofReaderShortenClick:
      return userClickEvents(interactions.proofReaderShortenClick(text))
    case origins.proofReaderRewriteSuggestionClick:
      return userClickEvents(
        interactions.proofReaderRewriteSuggestionClick(text, value, citationMetadata)
      )
    case origins.proofReaderExpandSuggestionClick:
      return userClickEvents(
        interactions.proofReaderExpandSuggestionClick(text, value, citationMetadata)
      )
    case origins.proofReaderShortenSuggestionClick:
      return userClickEvents(
        interactions.proofReaderShortenSuggestionClick(text, value, citationMetadata)
      )
    case origins.proofReaderFeedbackCardClick:
      return userClickEvents(
        interactions.proofReaderFeedbackCardClick(text, value, id, citationMetadata)
      )
    case origins.proofReaderRetryErrorClick:
      return userClickEvents(interactions.proofReaderRetryErrorClick(value))
    case origins.expandClarityReasonClick:
      return userClickEvents(interactions.expandClarityReasonClick(text, value, id))
    case origins.contractClarityReasonClick:
      return userClickEvents(interactions.contractClarityReasonClick(text, value, id))
    case origins.proofReaderSynonymClick:
      return userClickEvents(interactions.proofReaderSynonymClick(text))
    case origins.proofReaderSynonymSuggestionClick:
      return userClickEvents(interactions.proofReaderSynonymSuggestionClick(text, value))
    case origins.writingAssistantBannerClick:
      return userClickEvents(interactions.writingAssistantBannerClick())
    case origins.carousalImageGetStartedClick:
      return userClickEvents(interactions.carousalImageGetStartedClick(name, region))
    case origins.bannerTextGetStartedClick:
      return userClickEvents(interactions.bannerTextGetStartedClick(name, region))
    case origins.expandPaperListClick:
      return userClickEvents(interactions.expandPaperListClick())
    case origins.collapsePaperListClick:
      return userClickEvents(interactions.collapsePaperListClick())
    case origins.startNewPaperClick:
      return userClickEvents(interactions.startNewPaperClick())
    case origins.citationsLeftNavClick:
      return userClickEvents(interactions.citationsLeftNavClick())
    case origins.plagiarismLeftNavClick:
      return userClickEvents(interactions.plagiarismLeftNavClick())
    case origins.randomListPaperClick:
      return userClickEvents(interactions.randomListPaperClick())

    // create account banner click event
    case origins.citationValidityCreateAccountClick:
      return userClickEvents(
        interactions.citationValidityCreateAccountClick(citationMetadata)
      )
    case origins.saveAllCitationsCreateAccountClick:
      return userClickEvents(interactions.saveAllCitationsCreateAccountClick())

    case origins.undoRedoClick:
      return userClickEvents(
        interactions.undoRedoClick(name, type, interactionType, value, text, id)
      )

    default:
  }
}

export const clickStreamSignUpStartEvent: EventDefinition = (action: {
  type: typeof INSTRUMENTATION_SIGNUP_START | typeof VIEW_AUTH_SIGN_UP_MODE
  payload?: SignupStartPayload
}) => {
  const { type, payload } = action
  let data = {}

  if (type === INSTRUMENTATION_SIGNUP_START) {
    const { region, type: elementType } = payload
    data = {
      element: {
        name: signUpStartElementName,
        type: elementType ?? linkType,
        region_name: region,
      },
    }
  }

  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_SIGN_UP_START,
      data,
    },
    config: {
      eventVersion: 3,
    },
  }
}

export const clickStreamSignUpSuccessEvent: EventDefinition = (
  action,
  prevState
): EventWithConfig => {
  const { authService } = action.payload
  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_SIGN_UP_SUCCESS,
      data: {
        sign_up: {
          auth_service: authService,
          member_role: getSelectedUserTypeSelector(prevState),
          school_type: getSelectedSchoolTypeSelector(prevState),
        },
      },
    },
    config: {
      eventVersion: 3,
    },
  }
}

export const clickStreamSignInStartEvent: EventDefinition = (action: {
  type: typeof INSTRUMENTATION_SIGNIN_START | typeof VIEW_AUTH_SIGN_IN_MODE
  payload?: SigninStartPayload
}): EventWithConfig => {
  let data = {}

  if (action.type === INSTRUMENTATION_SIGNIN_START) {
    const { name, type, region_name } = action.payload
    data = {
      element: {
        name,
        type,
        region_name,
      },
    }
  }

  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_SIGN_IN_START,
      data,
    },
    config: {
      eventVersion: 3,
    },
  }
}

export const clickStreamSignInSuccessEvent: EventDefinition = (
  action
): EventWithConfig => {
  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_SIGN_IN_SUCCESS,
      data: {
        sign_in: {
          auth_service: action.payload.authService,
        },
      },
    },
    config: {
      eventVersion: 3,
    },
  }
}

export const clickStreamNotificationEvent: EventDefinition = (
  action
): EventWithConfig => {
  const { type, message_name, message_detail, element } = action.payload

  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_NOTIFICATIONS,
      data: {
        notifications: {
          type,
          message_name,
          message_detail,
          element,
        },
      },
    },
    config: {
      eventVersion: 3,
    },
  }
}

export const clickStreamSuccessEvent: EventDefinition = (action): EventWithConfig => {
  const { viewName: name, value, state, style: citation_style } = action.payload
  return {
    payload: {
      type: EVENT_TYPES.CLICKSTREAM_SUCCESS,
      data: {
        name,
        value,
        state,
        content: {
          metadata: {
            citation_style,
          },
        },
      },
    },
    config: {
      eventVersion: 3,
    },
  }
}

export const clickStreamComponentViewEvent: EventDefinition = (action: {
  payload: ComponentPayload
}): EventWithConfig => {
  const { origin, region, text, value, id, metadata } = action.payload

  switch (origin) {
    // improved book/journal search
    case componentOrigins.doiLink:
      return userComponentEvents(componentViews.doiLink(region))
    case componentOrigins.isbnLink:
      return userComponentEvents(componentViews.isbnLink(region))
    case componentOrigins.unlockModalOpen:
      return userComponentEvents(componentViews.unlockModalOpen())
    case componentOrigins.ucfUpgradeModalOpen:
      return userComponentEvents(componentViews.ucfUpgradeModalOpen(metadata))
    case componentOrigins.exportToWordOpen:
      return userComponentEvents(componentViews.exportToWordOpen(text))
    case componentOrigins.citationLimitApproaching:
      return userComponentEvents(componentViews.citationLimitApproaching(text))
    case componentOrigins.citationLimitReached:
      return userComponentEvents(componentViews.citationLimitReached(text))
    case componentOrigins.bibPageExpiration:
      return userComponentEvents(componentViews.bibPageExpiration(text))
    case componentOrigins.priceExperimentModal:
      return userComponentEvents(componentViews.priceExperimentModal(text))
    case componentOrigins.changeCitationStyleModal:
      return userComponentEvents(componentViews.changeCitationStyleModal(metadata))
    case componentOrigins.newCitationListModal:
      return userComponentEvents(componentViews.newCitationListModal(metadata))
    case componentOrigins.foldersPageExpiration:
      return userComponentEvents(componentViews.foldersPageExpiration(text))
    case componentOrigins.citationValidityBanner:
      return userComponentEvents(componentViews.citationValidityBanner(metadata))
    case componentOrigins.createAccountBanner:
      return userComponentEvents(componentViews.createAccountBanner())
    case componentOrigins.proofReaderFeedbackCard:
      return userComponentEvents(
        componentViews.proofReaderFeedbackCard(text, value, id, metadata)
      )
    case componentOrigins.proofReaderSynonymMenu:
      return userComponentEvents(componentViews.proofReaderSynonymMenu(text, value))
    case componentOrigins.proofReaderReEnableReviewButton:
      return userComponentEvents(componentViews.proofReaderReEnableReviewButton())
    case componentOrigins.proofReaderParaphraseActionBar:
      return userComponentEvents(
        componentViews.proofReaderParaphraseActionBar(text, value, metadata)
      )
    default:
      break
  }
}
